import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { 
  CheckCircle2, 
  XCircle, 
  Clock, 
  AlertTriangle,
  Ticket,
  User,
  DollarSign,
  ArrowLeft,
  ShieldCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const VoucherVerify = () => {
  const { code } = useParams();
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    verifyVoucher();
  }, [code]);

  const verifyVoucher = async () => {
    try {
      const response = await axios.get(`${API}/vouchers/code/${code}`);
      setVoucher(response.data);
    } catch (err) {
      setError(err.response?.status === 404 ? "Voucher not found" : "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isExpired = (dateStr) => {
    return new Date(dateStr) < new Date();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Verifying voucher...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Verification Failed</h1>
            <p className="text-slate-600 mb-6">{error}</p>
            <p className="text-sm text-slate-500 mb-6">
              Code: <span className="font-mono font-bold">{code}</span>
            </p>
            <Link to="/">
              <Button variant="outline" className="gap-2">
                <ArrowLeft className="w-4 h-4" /> Back to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isValid = voucher?.status === "active" && !isExpired(voucher?.expires_at);
  const statusConfig = {
    active: { icon: CheckCircle2, color: "text-green-600", bg: "bg-green-100", label: "Active & Valid" },
    redeemed: { icon: CheckCircle2, color: "text-blue-600", bg: "bg-blue-100", label: "Fully Redeemed" },
    expired: { icon: Clock, color: "text-gray-600", bg: "bg-gray-100", label: "Expired" },
    cancelled: { icon: XCircle, color: "text-red-600", bg: "bg-red-100", label: "Cancelled" }
  };

  const status = isExpired(voucher?.expires_at) && voucher?.status === "active" 
    ? "expired" 
    : voucher?.status;
  const config = statusConfig[status] || statusConfig.active;
  const StatusIcon = config.icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 p-4">
      <div className="max-w-lg mx-auto pt-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-slate-900 to-amber-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold">SV</span>
            </div>
            <span className="font-semibold text-slate-900">Soar Your Visibility</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Voucher Verification
          </h1>
        </div>

        {/* Verification Result Card */}
        <Card className={`mb-6 border-2 ${isValid ? 'border-green-300' : 'border-slate-200'}`}>
          <CardContent className="p-0">
            {/* Status Header */}
            <div className={`p-6 ${isValid ? 'bg-green-50' : 'bg-slate-50'} border-b`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-14 h-14 ${config.bg} rounded-full flex items-center justify-center`}>
                    <StatusIcon className={`w-8 h-8 ${config.color}`} />
                  </div>
                  <div>
                    <div className={`text-lg font-bold ${config.color}`}>
                      {isValid ? "VALID VOUCHER" : config.label.toUpperCase()}
                    </div>
                    <div className="text-sm text-slate-500">
                      Verified at {new Date().toLocaleTimeString()}
                    </div>
                  </div>
                </div>
                {isValid && (
                  <ShieldCheck className="w-8 h-8 text-green-600" />
                )}
              </div>
            </div>

            {/* Voucher Details */}
            <div className="p-6 space-y-4">
              {/* Code */}
              <div className="flex items-center justify-between py-3 border-b border-slate-100">
                <div className="flex items-center gap-2 text-slate-500">
                  <Ticket className="w-4 h-4" />
                  <span>Voucher Code</span>
                </div>
                <span className="font-mono text-lg font-bold text-slate-900">{voucher?.code}</span>
              </div>

              {/* Beneficiary */}
              <div className="flex items-center justify-between py-3 border-b border-slate-100">
                <div className="flex items-center gap-2 text-slate-500">
                  <User className="w-4 h-4" />
                  <span>Beneficiary</span>
                </div>
                <span className="font-medium text-slate-900">{voucher?.beneficiary_name}</span>
              </div>

              {/* Available Balance */}
              <div className="flex items-center justify-between py-3 border-b border-slate-100">
                <div className="flex items-center gap-2 text-slate-500">
                  <DollarSign className="w-4 h-4" />
                  <span>Available Balance</span>
                </div>
                <span className={`text-2xl font-bold ${isValid ? 'text-green-600' : 'text-slate-400'}`}>
                  {formatCurrency(voucher?.remaining_amount)}
                </span>
              </div>

              {/* Original Value */}
              <div className="flex items-center justify-between py-3 border-b border-slate-100">
                <span className="text-slate-500">Original Value</span>
                <span className="text-slate-600">{formatCurrency(voucher?.amount)}</span>
              </div>

              {/* Expiration */}
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-2 text-slate-500">
                  <Clock className="w-4 h-4" />
                  <span>Expires</span>
                </div>
                <span className={isExpired(voucher?.expires_at) ? 'text-red-600 font-medium' : 'text-slate-600'}>
                  {formatDate(voucher?.expires_at)}
                </span>
              </div>
            </div>

            {/* Action Footer */}
            {isValid && (
              <div className="p-6 bg-amber-50 border-t border-amber-100">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div className="text-sm">
                    <div className="font-semibold text-amber-800 mb-1">Partner Instructions</div>
                    <p className="text-amber-700">
                      Process payment up to the available balance shown above. 
                      Log into the partner portal to complete redemption and receive reimbursement.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-slate-500">
          <p>Soar Your Visibility - Government Voucher Program</p>
          <Link to="/dashboard" className="text-amber-600 hover:underline">
            Partner Portal Access
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VoucherVerify;
